import React from 'react';
import { useTranslation } from 'react-i18next';
import { TeamUser } from '../types/team.types';
import Button from '@/components/ui/button/Button';

interface TeamTableProps {
  teamUsers: TeamUser[];
  loading?: boolean;
  onEditUser: (userId: string) => void;
}

export const TeamTable: React.FC<TeamTableProps> = ({
  teamUsers,
  loading = false,
  onEditUser
}) => {
  const { t } = useTranslation();

  const getRoleDisplayName = (role: string) => {
    return t(`team.role.${role}`);
  };

  const getFullName = (user: TeamUser) => {
    return `${user.first_name} ${user.last_name}`.trim() || user.username;
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-stroke bg-white dark:bg-gray-800 dark:border-gray-700 px-5 pt-6 pb-2.5 shadow-default sm:px-7.5 xl:pb-1">
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-stroke bg-white dark:bg-gray-800 dark:border-gray-700 px-5 pt-6 pb-2.5 shadow-default sm:px-7.5 xl:pb-1">
      <div className="max-w-full overflow-x-auto">
        <table className="w-full table-auto">
          <thead className="border-b border-[#eee] dark:border-gray-700">
            <tr>
              <th className="text-left py-4 px-4 font-medium text-black dark:text-white">
                {t('team.columns.name')}
              </th>
              <th className="text-left py-4 px-4 font-medium text-black dark:text-white">
                {t('team.columns.email')}
              </th>
              <th className="text-left py-4 px-4 font-medium text-black dark:text-white">
                {t('team.columns.role')}
              </th>
              <th className="text-left py-4 px-4 font-medium text-black dark:text-white">
                {t('team.columns.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {teamUsers.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No team members found
                </td>
              </tr>
            ) : (
              teamUsers.map((user) => (
                <tr key={user.id} className="border-b border-[#eee] dark:border-gray-700 last:border-b-0">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-blue-500 text-white text-sm font-medium rounded-full">
                        {user.first_name ? user.first_name.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-black dark:text-white font-medium">
                          {getFullName(user)}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                          @{user.username}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <p className="text-black dark:text-white">
                      {user.email}
                    </p>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
                      user.role === 'admin' 
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' 
                        : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                    }`}>
                      {getRoleDisplayName(user.role)}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEditUser(user.id)}
                      className="text-sm"
                    >
                      {t('team.actions.edit')}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};